import { Link } from "react-router-dom";
import { LogoBadge } from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO, breadcrumbLd, webPageLd } from "@/lib/seo";

const TITLE = "Privacy Policy";
const DESC =
  "How DoppelCrush handles your selfie, face embedding, and personal data — in plain English.";

export default function Privacy() {
  return (
    <div className="crush-bg min-h-screen" data-testid="privacy-page">
      <SEO
        title={TITLE}
        description={DESC}
        path="/privacy"
        jsonLd={[
          webPageLd({ title: TITLE, description: DESC, path: "/privacy" }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Privacy", path: "/privacy" },
          ]),
        ]}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
        <Link to="/"><LogoBadge /></Link>
        <article className="crush-frame mt-8 p-6 sm:p-10 prose-style">
          <h1 className="font-display text-5xl font-bold leading-[0.95]">
            <span className="crush-text-grad">Privacy</span> Policy.
          </h1>
          <p className="mt-4 text-sm font-body text-slate-500">Last updated: 14 February 2026.</p>

          <h2 className="font-display text-2xl font-bold mt-8">What we collect</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1.5 font-body text-slate-700">
            <li>Your email and password (passwords are stored as bcrypt hashes — we never see them).</li>
            <li>Your name, age, gender and dating preference.</li>
            <li>One selfie that you upload. We store the image privately in object storage.</li>
            <li>A 128-dimensional face embedding generated <strong>in your browser</strong> using the open-source face-api.js library. Only the numeric embedding is sent to our server — never any biometric template Apple/Google could use to identify you.</li>
            <li>Your swipes, matches and chat messages with other users.</li>
            <li>Standard analytics events (page views, share clicks). No third-party ad trackers.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold mt-8">What we don&apos;t do</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1.5 font-body text-slate-700">
            <li>Sell your data. Ever.</li>
            <li>Run third-party ad networks on the app.</li>
            <li>Share your selfie with anyone other than users you&apos;ve actively shown it to (matches, Doppel Rooms you joined, twin pages you shared).</li>
            <li>Use your face embedding to train external face-recognition models.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold mt-8">Your controls</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1.5 font-body text-slate-700">
            <li>Replace your selfie at any time from <Link to="/profile" className="text-pink-600 underline">Profile</Link>.</li>
            <li>Delete your account from Profile. We wipe your selfie, embedding, swipes, matches and messages immediately.</li>
            <li>Pause sharing — you can revoke any public twin or Doppel Room link by deleting your account; rooms expire shortly after.</li>
            <li>Email <a href="mailto:hello@doppelcrush.com" className="text-pink-600 underline">hello@doppelcrush.com</a> for a data export or a privacy question.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold mt-8">Cookies &amp; storage</h2>
          <p className="mt-2 font-body text-slate-700">
            We use one functional cookie / localStorage entry to keep you signed in (your JWT). No
            advertising cookies. No cross-site trackers.
          </p>

          <h2 className="font-display text-2xl font-bold mt-8">Children</h2>
          <p className="mt-2 font-body text-slate-700">
            DoppelCrush is strictly 18+. We verify age at onboarding and remove anyone we believe
            to be under 18. If you see a profile you believe belongs to a minor, please email us
            immediately.
          </p>

          <h2 className="font-display text-2xl font-bold mt-8">Contact</h2>
          <p className="mt-2 font-body text-slate-700">
            Email <a href="mailto:hello@doppelcrush.com" className="text-pink-600 underline">hello@doppelcrush.com</a> for any privacy concern.
            See <Link to="/safety" className="text-pink-600 underline">Safety</Link> for product-level protections.
          </p>
        </article>
      </div>
      <Footer />
    </div>
  );
}
